/* eslint-disable prettier/prettier */
'use client';

import { DetailedHTMLProps, ForwardedRef, forwardRef, InputHTMLAttributes, ReactNode, useMemo, useRef } from 'react';

import Image from 'next/image';
import { v4 } from 'uuid';

import cn from '@/helpers/utils/classNames';

import './style.css';

export type TextInputTypes = 'text' | 'number' | 'email' | 'password' | 'textarea' | 'date';
type CustomInput = Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'type' | 'size'>;
export interface TextInputProps extends CustomInput {
  type: TextInputTypes;
  onMount?: () => void;
  label?: string | ReactNode;
  inputClassName?: string;
  error?: string;
  message?: string;
  labelClassName?: string;
  messageClassName?: string;
  leftIcon?: any;
  rightIcon?: any;
  showAsterisk?: boolean;
  rightIconComponent?: ReactNode;
  leftIconClassName?: string;
  rightIconClassName?: string;
}

const TextInput = forwardRef<
  { validate: () => Promise<boolean> } & Partial<HTMLInputElement | HTMLTextAreaElement>,
  TextInputProps
>(function TextInput(props, ref) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>();

  const {
    type,
    label,
    className,
    onMount,
    inputClassName,
    labelClassName,
    messageClassName,
    onChange,
    message,
    value,
    error,
    leftIcon,
    rightIcon,
    showAsterisk = false,
    rightIconComponent,
    leftIconClassName,
    rightIconClassName,
    ...restProps
  } = props;

  const textId = useMemo(() => v4(), []);

  return (
    <div
      className={cn(
        `v2-font-sweet-sans-pro v2-relative v2-h-fit v2-w-full v2-text-base v2-flex v2-flex-col v2-gap-[6px]`,
        className
      )}
    >
      {label && (
        <label
          htmlFor={textId}
          className={cn('v2-font-sweet-sans-pro v2-font-normal v2-text-sm v2-text-gray-700', labelClassName)}
        >
          {label} {showAsterisk && <span className="v2-text-red-500">*</span>}
        </label>
      )}
      <div className="v2-relative">
        {leftIcon && (
          <Image
            src={leftIcon}
            alt="Left Icon"
            className={cn('v2-absolute v2-left-3 v2-top-[11px]', leftIconClassName)}
          />
        )}
        <input
          id={textId}
          ref={(ref as ForwardedRef<HTMLInputElement>) ?? (inputRef as ForwardedRef<HTMLInputElement>)}
          type={type}
          className={cn(
            'v2-w-full v2-border v2-rounded v2-py-2 v2-px-3 v2-text-base v2-font-sweet-sans-pro v2-text-gray-900 disabled:v2-text-gray v2-font-light placeholder:v2-text-gray-500 hover:v2-border-primary-500 focus-visible:v2-border-primary-600 focus-visible:v2-outline-none focus-visible:v2-shadow-input-focus-primary disabled:v2-bg-gray-50 disabled:v2-border-gray-300 disabled:v2-cursor-not-allowed',
            { 'v2-pl-10': leftIcon },
            { 'v2-pr-10': rightIcon },
            { 'v2-appearance-remove': type === 'number' },
            { 'v2-input-type-date': type === 'date' },
            { 'v2-border !v2-border-red-500 focus:!v2-border-red-500 focus:!v2-shadow-none': error },
            inputClassName
          )}
          value={value}
          onChange={e => onChange && onChange(e)}
          {...restProps}
        />
        {rightIconComponent}
        {rightIcon && (
          <Image
            src={rightIcon}
            alt="Right Icon"
            className={cn('v2-absolute v2-right-3 v2-top-[11px]', rightIconClassName)}
          />
        )}
      </div>
      {error && <p className="v2-font-sweet-sans-pro v2-text-sm v2-text-red-600 v2-font-light">{error}</p>}
      {message && (
        <p className={cn('v2-font-sweet-sans-pro v2-text-sm v2-text-gray-600 v2-font-light', messageClassName)}>
          {message}
        </p>
      )}
    </div>
  );
});

export default TextInput;
